import { useEffect, useState } from 'react'
import { Alert } from 'reactstrap'
import { revokeVehicleCerts, VehicleCert, VehicleCertQuery } from '../../services/endpoints/service29'
import useCertList from './useCertList'
import DataGrid, {
  Column,
  LoadPanel,
  Paging,
  Sorting
} from 'devextreme-react/data-grid'
import { certSortOrderFor, handleCertSortChange, makeStatusRenderer, renderDate, renderUser } from './Service29Utils'
import CertPager from './CertPager'
import PageSizeSelect from './PageSizeSelect'
import CertSelectAll from './CertSelectAll'
import { Service29CancelButton } from './Service29CancelButton'
import CertFilters, {
  CertDateRange,
  CertFilterField,
  CertFilterValues,
  CertStatusOption,
  dateRangeParams
} from './CertFilters'

const ACTIVE_STATUSES = ['issued', 'installed', 'pending_revocation']

const BASE_QUERY: VehicleCertQuery = {
  status: ACTIVE_STATUSES,
  sort: 'issuedAt',
  order: 'desc',
  max: 50,
  offset: 0
}

const FILTER_FIELDS: CertFilterField[] = [
  'vehicleVin',
  'deviceSerial',
  'issuedByUsername'
]

const STATUS_OPTIONS: CertStatusOption[] = [
  { value: '', label: 'All Statuses' },
  { value: 'issued', label: 'Issued' },
  { value: 'installed', label: 'Installed' },
  { value: 'pending_revocation', label: 'Pending Revocation' }
]

const DATE_RANGES: CertDateRange[] = [
  { field: 'issuedAt', label: 'Issued At' },
  { field: 'certExpiresAt', label: 'Expires At' }
]

const renderStatus = makeStatusRenderer({
  issued: { label: 'Issued', color: 'success' },
  installed: { label: 'Installed', color: 'primary' },
  pending_revocation: { label: 'Pending Revocation', color: 'warning' }
})

const Service29ActiveTab = () => {
  const [query, setQuery] = useState<VehicleCertQuery>(BASE_QUERY)
  const { certs, total, error, refresh } = useCertList(true, query)
  const [alert, setAlert] = useState<{ message: string; color: string } | null>(
    null
  )
  const [selectedCerts, setSelectedCerts] = useState<VehicleCert[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  useEffect(() => {
    setSelectedCerts([])
  }, [query])

  const toggleCert = (cert: VehicleCert) => {
    setSelectedCerts((prev) =>
      prev.some((c) => c.id === cert.id)
        ? prev.filter((c) => c.id !== cert.id)
        : [...prev, cert]
    )
  }

  const handleSearch = (filters: CertFilterValues) => {
    setQuery((prev) => ({
      ...prev,
      vehicleVin: filters.vehicleVin.trim() || undefined,
      deviceSerial: filters.deviceSerial.trim() || undefined,
      issuedByUsername: filters.issuedByUsername.trim() || undefined,
      ...dateRangeParams(filters.dates),
      status: filters.status ? [filters.status] : ACTIVE_STATUSES,
      offset: 0
    }))
  }

  const handleBulkRevoke = async () => {
    if (!window.confirm(`Revoke ${selectedCerts.length} cert(s)?`)) return
    setBulkLoading(true)

    const ids = selectedCerts.map((c) => c.id)
    await revokeVehicleCerts(ids)
    await refresh()
    setSelectedCerts([])
    setBulkLoading(false)
    setAlert({
      message: `Successfully initiated revocation for ${ids.length} cert(s)`,
      color: 'success'
    })
  }

  return (
    <section className="cert-tab-section">
      {(alert || error) && (
        <Alert color={alert?.color ?? 'danger'} toggle={() => setAlert(null)}>
          {alert?.message ?? error}
        </Alert>
      )}
      <CertFilters
        fields={FILTER_FIELDS}
        statusOptions={STATUS_OPTIONS}
        dateRanges={DATE_RANGES}
        onSearch={handleSearch}
      />
      <menu className="cert-tab-toolbar">
        <li>
          {selectedCerts.length > 0 && (
            <button
              className="cert-bulk-action revoke"
              disabled={bulkLoading}
              onClick={handleBulkRevoke}
            >
              {bulkLoading
                ? 'Revoking…'
                : `Revoke Selected (${selectedCerts.length})`}
            </button>
          )}
        </li>
        <li>
          <PageSizeSelect
            value={query.max ?? 50}
            onChange={(max) =>
              setQuery((prev) => ({ ...prev, max, offset: 0 }))
            }
          />
        </li>
      </menu>
      <DataGrid
        dataSource={certs}
        keyExpr="id"
        showBorders={true}
        rowAlternationEnabled={true}
        columnAutoWidth={true}
        allowColumnResizing={true}
        onOptionChanged={(e) => handleCertSortChange(e, setQuery, BASE_QUERY)}
      >
        <LoadPanel enabled />
        <Paging enabled={false} />
        <Sorting mode="single" />
        <Column
          width={70}
          allowSorting={false}
          allowResizing={false}
          caption="Cancel"
          cellRender={({ data }: { data: VehicleCert }) => {
            if (data.status === 'issued') {
              return (
                <Service29CancelButton
                  cert={data}
                  onSuccess={async () => {
                    await refresh()
                    setAlert({
                      message: 'Successfully cancelled vehicle cert',
                      color: 'success'
                    })
                  }}
                  onError={() =>
                    setAlert({
                      message: 'Failed to cancel vehicle cert',
                      color: 'danger'
                    })
                  }
                />
              )
            }
            if (data.status === 'pending_revocation') {
              return (
                <Service29CancelButton
                  cert={data}
                  confirmationMessage="Are you sure you want to cancel the pending cert revocation?"
                  onSuccess={async () => {
                    await refresh()
                    setAlert({
                      message: 'Successfully cancelled pending revocation',
                      color: 'success'
                    })
                  }}
                  onError={() =>
                    setAlert({
                      message: 'Failed to cancel pending revocation',
                      color: 'danger'
                    })
                  }
                />
              )
            }
            return null
          }}
        />
        <Column
          dataField="vehicle.displayName"
          caption="Vehicle"
          allowSorting={false}
        />
        <Column dataField="vehicle.vin" caption="VIN" allowSorting={false} />
        <Column dataField="device.serial" caption="Device" allowSorting={false} />
        <Column
          dataField="status"
          caption="Status"
          cellRender={renderStatus}
          sortOrder={certSortOrderFor(query, 'status')}
        />
        <Column
          dataField="issuedBy"
          caption="Issued By"
          cellRender={renderUser}
          allowSorting={false}
        />
        <Column
          dataField="issuedAt"
          caption="Issued At"
          cellRender={renderDate}
          sortOrder={certSortOrderFor(query, 'issuedAt')}
        />
        <Column
          dataField="certExpiresAt"
          caption="Expires At"
          cellRender={renderDate}
          sortOrder={certSortOrderFor(query, 'certExpiresAt')}
        />
        <Column
          width={100}
          allowSorting={false}
          headerCellRender={() => (
            <CertSelectAll
              certs={certs}
              eligibleStatus="installed"
              variant="revoke"
              label="Select All"
              selectedCerts={selectedCerts}
              onChange={setSelectedCerts}
            />
          )}
          cellRender={({ data }: { data: VehicleCert }) => {
            if (data.status !== 'installed') return null
            const selected = selectedCerts.some((c) => c.id === data.id)
            return (
              <>
                <input
                  type="checkbox"
                  id={`cert-select-${data.id}`}
                  className="cert-select-check revoke"
                  checked={selected}
                  onChange={() => toggleCert(data)}
                />
                <label htmlFor={`cert-select-${data.id}`}>Revoke</label>
              </>
            )
          }}
        />
      </DataGrid>
      <CertPager
        total={total}
        offset={query.offset ?? 0}
        max={query.max ?? 50}
        onPageChange={(offset) => setQuery((prev) => ({ ...prev, offset }))}
      />
    </section>
  )
}

export default Service29ActiveTab
